/** Pending agent reveal requests. Admins approve or deny; others see nothing unless linked here. */

import { Check, KeyRound, X } from 'lucide-react'
import { toast } from 'sonner'
import { useDecideSecretRevealRequest, useSecretRevealRequests } from '../../hooks/use-secrets'
import { useT } from '../../i18n'
import { useAppStore } from '../../stores/app-store'
import { ApiError } from '../../lib/api-client'
import { cn } from '../../lib/utils'
import { buttonDangerClass, buttonPrimaryClass, cardClass, errorTextClass, hintClass } from '../ui/styles'
import { formatSecretTimestamp, secretErrorMessage } from './secret-dashboard-utils'

interface SecretRevealRequestsPanelProps {
  /** Request id from `?request=<id>` (the approve link handed to the agent) */
  highlightId: string | null
  onDecided: (id: string) => void
}

export function SecretRevealRequestsPanel({ highlightId, onDecided }: SecretRevealRequestsPanelProps) {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const requestsQuery = useSecretRevealRequests('pending')
  const decide = useDecideSecretRevealRequest()
  const requests = requestsQuery.data?.requests ?? []
  const forbidden = requestsQuery.error instanceof ApiError && requestsQuery.error.status === 403
  const highlightMissing = Boolean(highlightId) && !requestsQuery.isLoading && !requestsQuery.error && !requests.some((item) => item.id === highlightId)

  if (!highlightId && (forbidden || requests.length === 0)) return null

  async function handleDecision(id: string, decision: 'approve' | 'deny') {
    try {
      await decide.mutateAsync({ id, decision })
      toast.success(decision === 'approve' ? t('secrets.requests.approved') : t('secrets.requests.denied'))
      onDecided(id)
    } catch (err) {
      toast.error(t('secrets.requests.decideFailed'), { description: secretErrorMessage(err, t) })
    }
  }

  return (
    <section aria-labelledby="secret-reveal-requests-title" className={cn(cardClass, 'space-y-3 border-warning/40 p-4')}>
      <div>
        <h2 id="secret-reveal-requests-title" className="flex items-center gap-2 text-sm font-semibold text-fg">
          <KeyRound className="h-4 w-4 text-warning" aria-hidden="true" /> {t('secrets.requests.title')}
        </h2>
        <p className={cn(hintClass, 'mt-1')}>{t('secrets.requests.hint')}</p>
      </div>

      {forbidden ? (
        <p className="text-sm text-fg-subtle">{t('secrets.requests.adminOnly')}</p>
      ) : requestsQuery.error ? (
        <p role="alert" className={errorTextClass}>{t('secrets.requests.loadError', { message: secretErrorMessage(requestsQuery.error, t) })}</p>
      ) : requestsQuery.isLoading ? (
        <p className="text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : (
        <div className="space-y-2">
          {highlightMissing ? <p className="text-sm text-fg-subtle">{t('secrets.requests.notPending')}</p> : null}
          <ul className="space-y-2">
            {requests.map((request) => {
              const label = request.key ?? request.secretEntryId
              return (
                <li
                  key={request.id}
                  className={cn(
                    'flex flex-col gap-3 rounded-md border px-3 py-2 sm:flex-row sm:items-start sm:justify-between',
                    request.id === highlightId ? 'border-accent bg-accent-soft' : 'border-border',
                  )}
                >
                  <div className="min-w-0 space-y-0.5 text-xs text-fg-muted">
                    <div className="break-all font-mono text-sm font-semibold text-fg">{label}</div>
                    <div className="break-all text-fg-subtle">{request.environment ?? '?'}{request.path ?? ''}</div>
                    <div className="break-words">{t('secrets.requests.apiKey', { name: request.apiKeyName ?? request.apiKeyId })}</div>
                    <div className="break-words">{t('secrets.requests.reason', { reason: request.reason })}</div>
                    <div className="text-fg-subtle">
                      {t('secrets.requests.timing', {
                        requested: formatSecretTimestamp(request.createdAt, t, locale),
                        expires: formatSecretTimestamp(request.expiresAt, t, locale),
                      })}
                    </div>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={decide.isPending}
                      onClick={() => void handleDecision(request.id, 'approve')}
                      aria-label={t('secrets.requests.approveNamed', { key: label })}
                      className={buttonPrimaryClass}
                    >
                      <Check className="h-4 w-4" aria-hidden="true" /> {t('secrets.requests.approve')}
                    </button>
                    <button
                      type="button"
                      disabled={decide.isPending}
                      onClick={() => void handleDecision(request.id, 'deny')}
                      aria-label={t('secrets.requests.denyNamed', { key: label })}
                      className={buttonDangerClass}
                    >
                      <X className="h-4 w-4" aria-hidden="true" /> {t('secrets.requests.deny')}
                    </button>
                  </div>
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </section>
  )
}
